import { useCallback, useReducer } from 'react';

import { NAV_STATE_LS_KEY } from '../../../../common/constants';
import type { Store, UserMenuData, UserSideNavData } from '../../../../common/types';

import type { LocalStorageState } from './types';

const nullState: LocalStorageState = {
	userSideNavData: { defaultWidth: undefined, defaultCollapsed: undefined },
	userMenuData: [],
};

// Allow the key to be extended (by adding a prefix or suffix)
let LS_KEY = NAV_STATE_LS_KEY;

export function useLocalSideNavUserData({ app }: { app: string }): Store {
	setLocalStorageKey(app);
	const [sideNavState, setSideNavState] = useReducer(reducer, getInitialState());

	const setUserMenuData = useCallback(async (userMenuData: UserMenuData) => {
		setSideNavState({ userMenuData });
	}, []);

	const setUserSideNavData = useCallback(
		async (userSideNavData: UserSideNavData) => {
			setSideNavState({ userSideNavData: { ...sideNavState.userSideNavData, ...userSideNavData } });
		},
		[sideNavState],
	);

	return {
		...sideNavState,
		setUserMenuData,
		setUserSideNavData,
	};
}

function setLocalStorageKey(suffix: string) {
	LS_KEY = [NAV_STATE_LS_KEY, suffix].join('-');
}

function getInitialState() {
	const storedState = parseLocalStorageState(localStorage.getItem(LS_KEY));

	if (storedState) {
		return storedState;
	}

	setLocalStorageState(nullState);
	return nullState;
}

function reducer(state: LocalStorageState, payload: Partial<LocalStorageState>): LocalStorageState {
	const newState = { ...state, ...payload };
	// Update localStorage with the new state
	setLocalStorageState(newState);
	return newState;
}

function setLocalStorageState(state: LocalStorageState) {
	localStorage.setItem(LS_KEY, JSON.stringify(state));
}

function parseLocalStorageState(state: string | null): LocalStorageState | undefined {
	if (!state) {
		return;
	}

	try {
		const parsedState = JSON.parse(state);

		// This check should return true for the nullState
		if (
			parsedState?.userSideNavData &&
			Array.isArray(parsedState.userMenuData) &&
			typeof parsedState.userSideNavData === 'object'
		) {
			return parsedState satisfies LocalStorageState;
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.log(error);
		// something went wrong with parsing, return undefined
	}
}
